import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  AverageLimit,
  So5LeaderboardSeasonality,
  SeasonEligibility,
} from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';
import { priceRarityVariables } from '@sorare/core/src/lib/scouting-center';

import { PriceCell } from '#src/components/searchPlayerTable/PriceCell/index.tsx';
import { SearchPlayerRow } from '#src/components/searchPlayerTable/SearchPlayerRow/index.tsx';
import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { usePrintablePlayersSearchClient } from '#src/hooks/search/usePrintablePlayersSearchClient.ts';
import type { extractQueryGeneric } from '#src/lib/searchClient.ts';
import { PlayerSearchLayout } from '#src/searchCommonPlayer/AdvancedPlayerSearch/PlayerSearchLayout.tsx';
import type { FullProps } from '#src/searchCommonPlayer/AdvancedPlayerSearch/index.tsx';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import type {
  AdvancedWatchlistFacetSearchQuery,
  AdvancedWatchlistFacetSearchQueryVariables,
  AdvancedWatchlistSearchQuery,
  AdvancedWatchlistSearchQueryVariables,
} from './__generated__/index.graphql.ts';

interface Props extends FullProps {
  watchlistId: string;
  canManageWatchlist?: boolean;
}

const ADVANCED_WATCHLISTS_SEARCH_QUERY = gql`
  query AdvancedWatchlistSearchQuery(
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $seasonEligibility: SeasonEligibility!
    $withLimitedMarketValue: Boolean!
    $withRareMarketValue: Boolean!
    $withSuperRareMarketValue: Boolean!
    $withUniqueMarketValue: Boolean!
    $inSeason: Boolean
    $onlyPrimary: Boolean!
    $withLimitedFloorPrice: Boolean!
    $withRareFloorPrice: Boolean!
    $withSuperRareFloorPrice: Boolean!
    $withUniqueFloorPrice: Boolean!
    $averageLimit: AverageLimit!
    $onlyFollowed: Boolean
    $lastOwnedCardsRarities: [Rarity!]
    $watchlistId: ID!
    $teamMode: AverageScoreTeamMode
  ) {
    market {
      id
      watchlist(id: $watchlistId) {
        id
        slug
        searchPlayers(
          query: $query
          page: $page
          pageSize: $pageSize
          sorts: $sorts
          advancedFilters: $advancedFilters
          refinements: $refinements
          facets: $facets
          onlyFollowed: $onlyFollowed
        ) {
          ...usePrintablePlayersSearchClient_searchPrintablePlayers
          commonPlayerHits {
            id
            ...PriceCell_commonPlayer
            anyPlayer {
              slug
              ...SearchPlayerRow_anyPlayer
            }
          }
        }
      }
    }
  }
  ${usePrintablePlayersSearchClient.fragments.searchPrintablePlayers}
  ${
    // oxlint-disable-next-line sorare/no-detached-fragments
    SearchPlayerRow.fragments.anyPlayer
  }
  ${
    // oxlint-disable-next-line sorare/no-detached-fragments
    PriceCell.fragments.commonPlayer
  }
` as TypedDocumentNode<
  AdvancedWatchlistSearchQuery,
  AdvancedWatchlistSearchQueryVariables
>;

type Query = typeof ADVANCED_WATCHLISTS_SEARCH_QUERY;

const ADVANCED_WATCHLISTS_FACET_SEARCH_QUERY = gql`
  query AdvancedWatchlistFacetSearchQuery(
    $field: String!
    $facetQuery: String!
    $query: String!
    $limit: Int
    $advancedFilters: String
    $refinements: [SearchRefinementInput!]!
    $onlyFollowed: Boolean
    $watchlistId: ID!
  ) {
    market {
      id
      watchlist(id: $watchlistId) {
        id
        slug
        user {
          slug
        }
        searchPlayerFacetValues(
          field: $field
          facetQuery: $facetQuery
          query: $query
          limit: $limit
          advancedFilters: $advancedFilters
          refinements: $refinements
          onlyFollowed: $onlyFollowed
        ) {
          ...usePrintablePlayersSearchClient_searchFacetValue
        }
      }
    }
  }
  ${usePrintablePlayersSearchClient.fragments.searchFacetValue}
` as TypedDocumentNode<
  AdvancedWatchlistFacetSearchQuery,
  AdvancedWatchlistFacetSearchQueryVariables
>;

type FacetQuery = typeof ADVANCED_WATCHLISTS_FACET_SEARCH_QUERY;

export const AdvancedWatchlistSearch = ({
  children,
  sport,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  initialIndexUIState,
  cardFilters,
  dropdownFilters,
  defaultHitsPerPage,
  watchlistId,
  lastOwnedCardsRarities,
  ...rest
}: Props) => {
  const { statsManager } = useManagePlayerSearchView();
  const index = defaultSort || rest.sorts[0];

  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();
  const searchClient = usePrintablePlayersSearchClient<Query, FacetQuery>(
    ADVANCED_WATCHLISTS_SEARCH_QUERY,
    {
      onlyFollowed: false,
      variables: {
        seasonEligibility:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON
            ? SeasonEligibility.IN_SEASON
            : SeasonEligibility.CLASSIC,
        inSeason:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON,
        onlyPrimary: false,
        ...priceRarityVariables(
          statsManager[PlayerSearchViewAttributes.MARKET_VALUE_RARITIES],
          statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY]
        ),
        averageLimit: AverageLimit.LAST_10,
        lastOwnedCardsRarities,
        sport,
        watchlistId: idFromObject(watchlistId),
      },
      fetchPolicy: 'network-only',
    },
    (data: extractQueryGeneric<Query>) => data?.market.watchlist?.searchPlayers,
    {
      fQuery: ADVANCED_WATCHLISTS_FACET_SEARCH_QUERY,
      extractSearchPlayersFacetValues: (
        data: extractQueryGeneric<FacetQuery>
      ) => data.market.watchlist?.searchPlayerFacetValues,
    }
  );

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{
        sport,
        withMyBidsBanner: false,
      }}
    >
      <SearchCardsContextProvider commonMarket legacyFilters={false}>
        <InstantCardSearch
          {...(index && { indexes: [index] })}
          availableSorts={rest.sorts}
          analyticsTags={analyticsTags}
          sport={sport}
          defaultFilters={defaultFilters}
          getOptionalFilters={getOptionalFilters}
          urlState
          distinct
          defaultHitsPerPage={100}
          searchClient={searchClient}
        >
          <PlayerSearchLayout
            cardFilters={cardFilters}
            dropdownFilters={dropdownFilters}
            sport={sport}
            watchlistId={watchlistId}
            lastOwnedCardsRarities={lastOwnedCardsRarities}
            {...rest}
          >
            {children}
          </PlayerSearchLayout>
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
